import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import ReportTabBtns from '../../tab/ReportTabBtns';
import { REPORT_TAB_TYPE } from '../../propTypes/reportTabProp';
import translate from '../../../../commonComponents/translate';
import ServerSideList from '../../../../commonComponents/lists/ServerSideList';
import UserColumn from '../../../../commonComponents/lists/columns/UserColumn';
import ReportFilters from './ReportFilters';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import { useModal } from '../../../../services/customHooks';
import DetailModal from './DetailModal';

// const concatenateFilters = (baseString, filters) => {
//   const filterParams = [];
//
//   for (const key in filters) {
//     if (filters[key] !== null) {
//       filterParams.push(`${key}=${filters[key]}`);
//     }
//   }
//
//   if (filterParams.length > 0) {
//     const filterString = filterParams.join('&');
//     return `${baseString}${filterString}`;
//   }
//   return baseString;
// };

const renderAccessBadges = access => {
  const filteredAccess = access?.filter(a => a);
  return renderToStaticMarkup(
    <div>
      {filteredAccess?.length > 0
        ? filteredAccess.map((a, i) => (
            <div
              key={a.name}
              style={{
                backgroundColor: a.color,
                marginBottom: i === 0 ? 0 : '5px',
              }}
              className="badge-access"
            >
              {a.name}
            </div>
          ))
        : ' - '}
    </div>,
  );
};

const FINAPP_CONFIG = {
  never: { icon: '✕', color: '#e74c3c', label: 'finAppNever' },
  freemium: { icon: '◎', color: '#f39c12', label: 'finAppFreemium' },
  premium: { icon: '★', color: '#27ae60', label: 'finAppPremium' },
};

const renderFinAppBadge = (t, finApp) => {
  const config = finApp ? FINAPP_CONFIG[finApp.toLowerCase()] : null;
  return renderToStaticMarkup(
    config ? (
      <span
        style={{ color: config.color, fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {config.icon} {t(`report.neofin.filter.${config.label}`)}
      </span>
    ) : (
      <span> - </span>
    ),
  );
};

const STATUS_CONFIG = {
  active: { color: '#22C55E', label: 'active' },
  neocard_only: { color: '#F59E0B', label: 'neocardOnly' },
  license_only: { color: '#4C6FFF', label: 'licenseOnly' },
  inactive: { color: '#EF4444', label: 'inactive' },
};

const LICENSE_STATUS_CONFIG = {
  active: { color: '#22C55E', label: 'licenseActive' },
  trial: { color: '#3B82F6', label: 'licenseTrial' },
  past_due: { color: '#F59E0B', label: 'licensePastDue' },
  canceled: { color: '#EF4444', label: 'licenseCanceled' },
  failed: { color: '#DC2626', label: 'licenseFailed' },
  failed_and_expired: { color: '#991B1B', label: 'licenseFailedAndExpired' },
  unpaid: { color: '#F97316', label: 'licenseUnpaid' },
};

const renderLicenseBadge = (
  t,
  licenseStatus,
  licenseNextRenewDate,
  licenseCancellationDate,
) => {
  const config = licenseStatus
    ? LICENSE_STATUS_CONFIG[licenseStatus.toLowerCase()]
    : null;
  if (!config) {
    return renderToStaticMarkup(<span> - </span>);
  }
  let date = null;
  if (licenseStatus === 'active' && licenseNextRenewDate) {
    date = moment(licenseNextRenewDate).format('DD MMM YYYY');
  } else if (licenseStatus === 'canceled' && licenseCancellationDate) {
    date = moment(licenseCancellationDate).format('DD MMM YYYY');
  }
  return renderToStaticMarkup(
    <div style={{ textAlign: 'center' }}>
      <span
        style={{ color: config.color, fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {t(`report.neofin.${config.label}`)}
      </span>
      {date ? (
        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
          {date}
        </div>
      ) : null}
    </div>,
  );
};

const renderStatusBadge = (t, status) => {
  const config = status ? STATUS_CONFIG[status.toLowerCase()] : null;
  return renderToStaticMarkup(
    config ? (
      <span
        style={{ color: config.color, fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {t(`report.neofin.${config.label}`)}
      </span>
    ) : (
      <span> - </span>
    ),
  );
};

function TeamReport(props) {
  const { t, countries } = props;
  const tableUrL = '/table/tailor-made/neofin/user-reports';
  const tableId = 'neofin_tailor_made';

  const [filters, setFilters] = useState({
    search: null,
    createDateStart: null,
    createDateEnd: null,
    shippingCountry: null,
    businessLicense: null,
    access: null,
    active: null,
    forecastLevel: null,
    level: null,
    sponsoredBy: null,
    referral: null,
    finAppStatus: null,
    vaGt0: null,
    developerOnly: null,
    noNeocard: null,
    noLicense: null,
    rankMin: null,
    rankMax: null,
  });
  const [detailModal, setDetailModal] = useModal({ show: false, data: null });

  const handleDetailModal = useCallback(
    (show, detail) => setDetailModal({ show, data: detail }),
    [setDetailModal],
  );

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      createDateStart: startDate,
      createDateEnd: endDate,
    }));
  };

  // const handleExportList = useCallback(() => {
  //   const endpoint = concatenateFilters(
  //     '/excel/tailor-made/neofin/user-reports/xlsx?',
  //     filters,
  //   );
  //   window.open(endpoint);
  // }, [filters]);

  const columns = [
    {
      field: 'createdAt',
      textAlign: 'left',
      title: t('created.at'),
      width: 100,
      sortable: 'desc',
      template: ({ createdAt }) =>
        renderToStaticMarkup(<>{moment(createdAt).format('DD MMM YYYY')}</>),
    },
    {
      field: 'firstName',
      textAlign: 'left',
      title: t('profile_firstName'),
      width: 100,
      sortable: true,
      template: ({ firstName, lastName, email }) =>
        renderToStaticMarkup(
          <div className="name">
            {!firstName && !lastName ? email : firstName || ' - '}
          </div>,
        ),
    },
    {
      field: 'lastName',
      textAlign: 'left',
      title: t('profile_lastName'),
      width: 100,
      sortable: true,
      template: ({ lastName }) =>
        renderToStaticMarkup(<div className="name">{lastName || ' - '}</div>),
    },
    {
      field: 'shippingCountry',
      textAlign: 'left',
      title: '🌍',
      width: 80,
      sortable: true,
      template: ({ shippingCountryInfo }) =>
        renderToStaticMarkup(
          shippingCountryInfo?.code ? (
            <img
              className="countryInfo"
              src={shippingCountryInfo?.flag?.medium}
              alt={shippingCountryInfo?.code}
            />
          ) : (
            ' - '
          ),
        ),
    },
    {
      field: 'sponsoredBy',
      textAlign: 'left',
      title: t('sponsor'),
      width: 150,
      sortable: false,
      template: ({ sponsoredBy }) =>
        renderToStaticMarkup(
          sponsoredBy ? (
            <UserColumn t={t} user={sponsoredBy} includeWrapper />
          ) : (
            ' - '
          ),
        ),
    },
    {
      field: 'status',
      textAlign: 'center',
      title: t('report.neofin.status'),
      width: 120,
      sortable: false,
      template: ({ status }) => renderStatusBadge(t, status),
    },
    {
      field: 'licenseStatus',
      textAlign: 'center',
      title: t('report.neofin.license'),
      width: 130,
      sortable: false,
      template: ({
        licenseStatus,
        licenseNextRenewDate,
        licenseCancellationDate,
      }) =>
        renderLicenseBadge(
          t,
          licenseStatus,
          licenseNextRenewDate,
          licenseCancellationDate,
        ),
    },
    {
      field: 'activePersonalDirects',
      textAlign: 'center',
      title: t('report.neofin.activeDirects'),
      width: 100,
      sortable: false,
      template: ({ activePersonalDirects }) =>
        renderToStaticMarkup(
          <span
            style={{
              color: activePersonalDirects >= 3 ? '#22C55E' : undefined,
              fontWeight: activePersonalDirects >= 3 ? 500 : undefined,
            }}
          >
            {activePersonalDirects ?? ' - '}
          </span>,
        ),
    },
    {
      field: 'access',
      textAlign: 'center',
      title: t('report.neofin.neocard'),
      width: 100,
      sortable: false,
      template: ({ access }) => renderAccessBadges(access),
    },
    {
      field: 'finApp',
      textAlign: 'center',
      title: t('report.neofin.finapp'),
      width: 100,
      sortable: false,
      template: ({ finApp }) => renderFinAppBadge(t, finApp),
    },
    {
      field: 'forecastLevelInfo',
      textAlign: 'center',
      title: t('report.neofin.rank'),
      width: 150,
      sortable: false,
      template: ({ forecastLevelInfo }) =>
        renderToStaticMarkup(
          <p className="level-info">{forecastLevelInfo?.name || ' - '}</p>,
        ),
    },
    {
      field: 'levelInfo',
      textAlign: 'center',
      title: t('report.neofin.rankMax'),
      width: 150,
      sortable: false,
      template: ({ levelInfo }) =>
        renderToStaticMarkup(
          <p className="level-info">{levelInfo?.name || ' - '}</p>,
        ),
    },
    {
      field: 'developerDate',
      textAlign: 'left',
      title: t('report.neofin.developerDate'),
      width: 120,
      sortable: false,
      template: ({ developerDate }) =>
        renderToStaticMarkup(
          <>
            {developerDate
              ? moment(developerDate).format('DD MMM YYYY')
              : ' - '}
          </>,
        ),
    },
  ];
  return (
    <>
      {detailModal.show && detailModal.data ? (
        <DetailModal
          user={detailModal.data}
          onClose={() => handleDetailModal(false, null)}
        />
      ) : null}
      <Title title={t('myReports')}>
        <ContentContainer>
          <SubHeader title={t('myReports')} />
          <TopicBanner />
          <div className="m-content" id="neofin-team-report">
            <div className="full-page-container-with-subheader">
              <div id="">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="m-portlet m-portlet--mobile">
                      <ReportTabBtns
                        tabActive={REPORT_TAB_TYPE.NEOFIN_TEAM_REPORT}
                      />
                      <ReportFilters
                        t={t}
                        onDateChange={handleDateChange}
                        onFilterChange={handleFiltersChange}
                        filters={filters}
                        countries={countries}
                      />
                      <div className="export-list">
                        <br />
                      </div>
                      {/* <div className="export-list"> */}
                      {/*   <button */}
                      {/*     type="button" */}
                      {/*     onClick={() => handleExportList()} */}
                      {/*     className="btn btn-excel-export btn-sm" */}
                      {/*   > */}
                      {/*     {t('export.excel')} */}
                      {/*   </button> */}
                      {/* </div> */}
                      <div className="list-wrapper">
                        <ServerSideList
                          dataSourceURL={tableUrL}
                          columns={columns}
                          query={filters}
                          id={tableId}
                          onRowClick={detail => handleDetailModal(true, detail)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

TeamReport.propTypes = {
  t: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
};
TeamReport.defaultProps = {
  countries: [],
};

export default translate(TeamReport);
